"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
interface FaqItem {
  id: string;
  question: string;
  answer: string;
}
const FAQ_ITEMS: FaqItem[] = [{
  id: "item-1",
  question: "How does the age and structural condition of Decatur homes influence project costs?",
  answer: "Many Decatur properties are historic bungalows, mid-century ranches, or Craftsman homes with unique architectural framing. Hidden challenges behind walls—such as legacy knob-and-tube wiring, galvanized plumbing, settled foundations, or unlevel subfloors—require corrective structural work before finishing trades can begin."
}, {
  id: "item-2",
  question: "What role do local zoning, historic district guidelines, and city permitting play?",
  answer: "Renovations located in designated local historic districts or requiring footprint expansion must align with Decatur's tree preservation ordinances, setback rules, and Historic Preservation Commission reviews. Navigating detailed architectural drawings, engineering reviews, and municipal inspections introduces critical planning phases that impact overall investment."
}, {
  id: "item-3",
  question: "How do layout alterations and moving utilities affect the overall budget?",
  answer: "Keeping plumbing stacks, gas lines, and load-bearing walls in their original locations reduces trade labor. Relocating kitchen sinks, moving bathroom drain lines through crawl spaces or slabs, and removing interior structural partitions to create open-concept living require engineered beams, HVAC rerouting, and extensive mechanical repositioning."
}, {
  id: "item-4",
  question: "How do material choices and custom millwork alter the scope?",
  answer: "Finishing materials represent a significant variable in every remodel. Selecting custom inset hardwood cabinetry, natural stone slab countertops, architectural tile layouts, specialized heritage mouldings, and commercial-grade appliances requires specialized craftspeople and tailored lead times compared to standard off-the-shelf selections."
}, {
  id: "item-5",
  question: "How does project scale—single room versus whole-home phasing—impact efficiency?",
  answer: "Consolidating multiple areas into a single coordinated construction phase reduces mobilization overhead, site protection setup, and repeat trade trips. Conversely, staging remodels across multiple sequential phases spreads out investment over time but adds protective dust barriers and separate setup logistics for each stage."
}];
export default function CostFactorsFaq() {
  return <section id="remodelingcostfactorsfaq" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-foreground mb-4">
            What Drives Remodeling Costs in Decatur
          </h2>
          <p className="font-default text-base sm:text-lg text-muted-foreground leading-relaxed">
            Understanding the structural, municipal, and design elements that shape renovation investments for historic and modern homes across the community.
          </p>
        </div>

        {/* Q&A Accordion */}
        <div className="bg-card/90 backdrop-blur-xs border border-border/80 rounded-xl shadow-sm p-4 sm:p-8 md:p-10 mb-12">
          <Accordion type="single" collapsible defaultValue="item-1" className="w-full divide-y divide-border/60">
            {FAQ_ITEMS.map((item, index) => <AccordionItem key={item.id} value={item.id} data-index={index} className="border-none py-2 first:pt-0 last:pb-0">
                <AccordionTrigger className="font-serif text-left text-lg sm:text-xl font-medium text-foreground hover:text-chart-1 transition-colors py-4 group">
                  <span className="pr-4">{item.question}</span>
                </AccordionTrigger>
                <AccordionContent className="font-default text-muted-foreground text-sm sm:text-base leading-relaxed pt-1 pb-4">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        {/* Closing Planning CTA */}
        <div className="text-center">
          <p className="font-default text-sm sm:text-base text-muted-foreground mb-4">
            Have questions regarding your specific home layout or planning requirements?
          </p>
          <Button asChild size="lg" className="rounded-md font-medium">
            <Link to="/ProcessPlanningPage">
              Explore Our Planning Process
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}